import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const LIJST_LABEL: Record<string, string> = {
  alle_email: "Allen met e-mail",
  informatief: "Informatief",
  overlijden: "Bij overlijden",
  "40_dagen": "40-dagenmail",
};

function keysBadge(keys: string[]) {
  if (keys.length === 0) return <span style={{ color: "var(--muted, #888)" }}>geen</span>;
  return <>{keys.map((k) => <span key={k} className="nav-badge" style={{ background: "var(--accent-soft, #e8f3ff)", color: "var(--accent, #0066cc)", marginRight: 4, padding: "1px 7px", borderRadius: 9999, fontSize: "0.78rem", fontWeight: 500 }}>{LIJST_LABEL[k] ?? k}</span>)}</>;
}

type SubscriberInfo = {
  ml_id: string;
  ml_email: string;
  ml_status: string;
  ml_first_name: string;
  ml_last_name: string;
  proposed_keys: string[];
};

type MatchedRow = SubscriberInfo & {
  contact_id: number;
  contact_name: string;
  current_keys: string[];
  will_change: boolean;
  will_link: boolean;
};

type UnmatchedRow = SubscriberInfo;

type PreviewData = {
  matched_update: MatchedRow[];
  matched_nochange: MatchedRow[];
  unmatched: UnmatchedRow[];
  api_error: string | null;
};

type ContactSearchResult = { id: number; first_name: string; last_name: string; tussenvoegsel?: string | null; entity_kind: string };

type Decision =
  | { type: "link"; contact_id: number; contact_name: string }
  | { type: "create" }
  | { type: "skip" };

function SearchBox({
  onSelect,
}: {
  onSelect: (c: ContactSearchResult) => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ContactSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback((v: string) => {
    if (!v.trim()) { setResults([]); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const r = await api.get<{ data: ContactSearchResult[] }>(`/api/contacts?search=${encodeURIComponent(v)}&limit=8&paginate=false`);
        setResults(r.data.data ?? []);
        setOpen(true);
      } catch { setResults([]); }
    }, 250);
  }, []);

  return (
    <div style={{ position: "relative", display: "inline-block", minWidth: 200 }}>
      <input
        className="form-input"
        style={{ fontSize: "0.85rem", padding: "0.3rem 0.6rem" }}
        placeholder="Zoeken op naam…"
        value={q}
        onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
      />
      {open && results.length > 0 && (
        <ul style={{
          position: "absolute", zIndex: 99, background: "#fff",
          border: "1px solid var(--border,#ddd)", borderRadius: 6,
          margin: 0, padding: "0.25rem 0", listStyle: "none",
          minWidth: 220, maxWidth: 320, boxShadow: "0 4px 16px rgba(0,0,0,.1)",
        }}>
          {results.map((c) => {
            const name = [c.first_name, c.tussenvoegsel, c.last_name].filter(Boolean).join(" ");
            return (
              <li key={c.id}
                onMouseDown={() => { onSelect(c); setQ(name); setOpen(false); }}
                style={{ padding: "0.4rem 0.75rem", cursor: "pointer", fontSize: "0.87rem" }}
              >
                <strong>{name}</strong>{" "}
                <span style={{ color: "#999", fontSize: "0.78rem" }}>#{c.id} · {c.entity_kind}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function MailerLiteImportPage() {
  const navigate = useNavigate();
  const [onlyActive, setOnlyActive] = useState(false);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [loadErr, setLoadErr] = useState("");
  const [preview, setPreview] = useState<PreviewData | null>(null);

  // decisions[ml_id] = Decision
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  // apply state
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ lists_updated: number; linked: number; created: number; errors: string[] } | null>(null);
  const [applyErr, setApplyErr] = useState("");
  const loadGenRef = useRef(0);

  async function loadPreview() {
    const gen = ++loadGenRef.current;
    setLoadState("loading");
    setLoadErr("");
    setPreview(null);
    setDecisions({});
    setApplyResult(null);
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    try {
      const start = await api.post<{ data: { job_id: number } }>("/api/mailerlite/import-preview-job", {
        only_active: onlyActive,
      });
      if (gen !== loadGenRef.current) return;
      const jobId = start.data.data.job_id;
      const maxPolls = 800;
      for (let polls = 0; polls < maxPolls; polls++) {
        const poll = await api.get<{
          data: { status: "pending" | "running" | "done" | "error"; result: PreviewData | null; error: string | null };
        }>(`/api/mailerlite/import-preview-job/${jobId}`);
        if (gen !== loadGenRef.current) return;
        const { status, result, error } = poll.data.data;
        if (status === "done" && result) {
          setPreview(result);
          const init: Record<string, Decision> = {};
          for (const r of result.unmatched) init[r.ml_id] = { type: "skip" };
          setDecisions(init);
          setLoadState("ready");
          return;
        }
        if (status === "error") {
          setLoadErr(error ?? "Import mislukt");
          setLoadState("error");
          return;
        }
        await sleep(1500);
      }
      if (gen !== loadGenRef.current) return;
      setLoadErr("Het laden duurt ongewoon lang. Vernieuw de pagina en probeer opnieuw.");
      setLoadState("error");
    } catch (e: unknown) {
      if (gen !== loadGenRef.current) return;
      const ax = e as { response?: { data?: { error?: string } } };
      setLoadErr(ax.response?.data?.error ?? "Laden mislukt");
      setLoadState("error");
    }
  }

  function setDecision(ml_id: string, d: Decision) {
    setDecisions((prev) => ({ ...prev, [ml_id]: d }));
  }

  async function applyDecisions() {
    if (!preview) return;
    setApplying(true);
    setApplyErr("");
    setApplyResult(null);

    const apply_updates = preview.matched_update.map((r) => r.ml_id);
    const link: { ml_id: string; contact_id: number }[] = [];
    const create: string[] = [];

    for (const [ml_id, d] of Object.entries(decisions)) {
      if (d.type === "link") link.push({ ml_id, contact_id: d.contact_id });
      if (d.type === "create") create.push(ml_id);
    }

    const allSubscribers: SubscriberInfo[] = [
      ...preview.matched_update,
      ...preview.matched_nochange,
      ...preview.unmatched,
    ];

    try {
      const res = await api.post<{ data: { lists_updated: number; linked: number; created: number; errors: string[] } }>(
        "/api/mailerlite/import-apply",
        { apply_updates, link, create, all_subscribers: allSubscribers }
      );
      setApplyResult(res.data.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setApplyErr(ax.response?.data?.error ?? "Toepassen mislukt");
    } finally {
      setApplying(false);
    }
  }

  const totalActions = preview
    ? preview.matched_update.length +
      Object.values(decisions).filter((d) => d.type === "link" || d.type === "create").length
    : 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <button className="btn-back" type="button" onClick={() => navigate("/admin/tools")}
            style={{ display: "inline-block", marginBottom: "0.5rem", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            ← Scripts
          </button>
          <h1 className="page-title">MailerLite: lijsten importeren</h1>
          <p className="page-subtitle" style={{ maxWidth: 640 }}>
            Laad alle abonnees vanuit MailerLite. Bekijk wie al gekoppeld is en wie niet. Beslis per onbekende abonnee
            of je hem koppelt aan een bestaande kaart of een nieuwe aanmaakt. Klik daarna op{" "}
            <strong>Toepassen</strong>.
          </p>
        </div>
      </div>

      {/* Load controls */}
      {loadState !== "ready" && (
        <div className="detail-section" style={{ maxWidth: 560, marginBottom: "1.5rem" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", cursor: "pointer" }}>
            <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} disabled={loadState === "loading"} />
            Alleen actieve abonnees ophalen (sneller; uitgeschreven adressen niet bijgewerkt)
          </label>
          <button className="btn-primary" type="button" onClick={() => void loadPreview()} disabled={loadState === "loading"}>
            {loadState === "loading" ? "Bezig met ophalen uit MailerLite…" : "Ophalen en bekijken"}
          </button>
          {loadErr && <p className="form-error" style={{ marginTop: "0.75rem" }}>{loadErr}</p>}
        </div>
      )}

      {loadState === "loading" && (
        <p className="page-subtitle" style={{ marginTop: "1rem" }}>
          Even geduld — MailerLite wordt pagina voor pagina opgehaald…
        </p>
      )}

      {loadState === "ready" && preview && (
        <>
          {preview.api_error && (
            <p className="form-error" style={{ marginBottom: "1rem" }}>
              MailerLite API-fout: {preview.api_error}
            </p>
          )}

          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.5rem", alignItems: "center" }}>
            <button className="btn-secondary" type="button" onClick={() => void loadPreview()}>↺ Opnieuw ophalen</button>
            <span className="page-subtitle" style={{ margin: 0 }}>
              Bijwerken: <strong>{preview.matched_update.length}</strong> · Geen wijziging: {preview.matched_nochange.length} · Onbekend: <strong>{preview.unmatched.length}</strong>
            </span>
          </div>

          {/* ── Bijwerken ── */}
          {preview.matched_update.length > 0 && (
            <section style={{ marginBottom: "2rem" }}>
              <h2 className="section-title" style={{ marginBottom: "0.75rem" }}>
                Bijwerken ({preview.matched_update.length})
              </h2>
              <p className="page-subtitle" style={{ marginBottom: "0.75rem", marginTop: 0 }}>
                Deze kaarten zijn gekoppeld en de e-maillijsten verschillen. Ze worden bijgewerkt bij klikken op Toepassen.
              </p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ background: "var(--surface-100, #f8f9fa)" }}>
                      <th style={TH}>Abonnee</th>
                      <th style={TH}>Status</th>
                      <th style={TH}>Kaart</th>
                      <th style={TH}>Nu in DomAddress</th>
                      <th style={TH}>Wordt (MailerLite)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.matched_update.map((r) => (
                      <tr key={r.ml_id} style={{ borderBottom: "1px solid var(--border, #e5e7eb)" }}>
                        <td style={TD}>
                          <div style={{ fontWeight: 500 }}>{r.ml_first_name || r.ml_last_name ? `${r.ml_first_name} ${r.ml_last_name}`.trim() : "—"}</div>
                          <div style={{ fontSize: "0.8rem", color: "#666" }}>{r.ml_email}</div>
                        </td>
                        <td style={TD}><StatusBadge status={r.ml_status} /></td>
                        <td style={TD}>
                          <a href={`#`} style={{ color: "var(--accent, #0066cc)" }}>{r.contact_name}</a>
                          {r.will_link && <span style={{ fontSize: "0.75rem", color: "#888", marginLeft: 4 }}>(+ koppelen)</span>}
                        </td>
                        <td style={TD}>{keysBadge(r.current_keys)}</td>
                        <td style={TD}>{keysBadge(r.proposed_keys)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Onbekend ── */}
          {preview.unmatched.length > 0 && (
            <section style={{ marginBottom: "2rem" }}>
              <h2 className="section-title" style={{ marginBottom: "0.5rem" }}>
                Niet gevonden in DomAddress ({preview.unmatched.length})
              </h2>
              <p className="page-subtitle" style={{ marginBottom: "0.75rem", marginTop: 0 }}>
                Zoek een bestaande kaart om te koppelen, of kies &apos;Aanmaken&apos; voor een nieuwe
                kaart van het type <strong>E-mailabonnee</strong> (eigen categorie — geen vrijwilliger,
                dominicusganger, of andere bestaande lijst). &apos;Overslaan&apos; doet niets voor die abonnee.
              </p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ background: "var(--surface-100, #f8f9fa)" }}>
                      <th style={TH}>Abonnee in MailerLite</th>
                      <th style={TH}>Status</th>
                      <th style={TH}>Lijsten</th>
                      <th style={{ ...TH, minWidth: 260 }}>Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.unmatched.map((r) => {
                      const d = decisions[r.ml_id] ?? { type: "skip" };
                      return (
                        <tr key={r.ml_id} style={{ borderBottom: "1px solid var(--border, #e5e7eb)", background: d.type !== "skip" ? "var(--success-bg, #f0fff4)" : undefined }}>
                          <td style={TD}>
                            <div style={{ fontWeight: 500 }}>{r.ml_first_name || r.ml_last_name ? `${r.ml_first_name} ${r.ml_last_name}`.trim() : "—"}</div>
                            <div style={{ fontSize: "0.8rem", color: "#666" }}>{r.ml_email}</div>
                          </td>
                          <td style={TD}><StatusBadge status={r.ml_status} /></td>
                          <td style={TD}>{keysBadge(r.proposed_keys)}</td>
                          <td style={TD}>
                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                              {d.type === "link" ? (
                                <>
                                  <span style={{ fontSize: "0.85rem", color: "var(--accent, #0066cc)", fontWeight: 500 }}>
                                    → {d.contact_name}
                                  </span>
                                  <button type="button" className="btn-secondary" style={{ fontSize: "0.78rem", padding: "2px 8px" }}
                                    onClick={() => setDecision(r.ml_id, { type: "skip" })}>✕ Ontkoppelen</button>
                                </>
                              ) : d.type === "create" ? (
                                <>
                                  <span style={{ fontSize: "0.85rem", color: "green", fontWeight: 500 }}>Aanmaken</span>
                                  <button type="button" className="btn-secondary" style={{ fontSize: "0.78rem", padding: "2px 8px" }}
                                    onClick={() => setDecision(r.ml_id, { type: "skip" })}>✕ Annuleren</button>
                                </>
                              ) : (
                                <>
                                  <SearchBox onSelect={(c) => {
                                    const name = [c.first_name, c.tussenvoegsel, c.last_name].filter(Boolean).join(" ");
                                    setDecision(r.ml_id, { type: "link", contact_id: c.id, contact_name: name });
                                  }} />
                                  <button type="button" className="btn-secondary" style={{ fontSize: "0.82rem", padding: "3px 10px", whiteSpace: "nowrap" }}
                                    onClick={() => setDecision(r.ml_id, { type: "create" })}>
                                    + Aanmaken (e-mailabonnee)
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Geen wijziging (collapsed) ── */}
          {preview.matched_nochange.length > 0 && (
            <details style={{ marginBottom: "1.5rem" }}>
              <summary className="section-title" style={{ cursor: "pointer", userSelect: "none" }}>
                Al gelijk — geen actie nodig ({preview.matched_nochange.length})
              </summary>
              <div style={{ overflowX: "auto", marginTop: "0.75rem" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ background: "var(--surface-100, #f8f9fa)" }}>
                      <th style={TH}>Abonnee</th>
                      <th style={TH}>Kaart</th>
                      <th style={TH}>Lijsten (al gelijk)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.matched_nochange.map((r) => (
                      <tr key={r.ml_id} style={{ borderBottom: "1px solid var(--border, #e5e7eb)" }}>
                        <td style={TD}>{r.ml_email}</td>
                        <td style={TD}>{r.contact_name}</td>
                        <td style={TD}>{keysBadge(r.proposed_keys)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* ── Apply bar ── */}
          {!applyResult && (
            <div style={{
              position: "sticky", bottom: 0, background: "var(--bg, #fff)", borderTop: "2px solid var(--border, #ddd)",
              padding: "1rem 0", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap",
            }}>
              {applyErr && <p className="form-error" style={{ margin: 0 }}>{applyErr}</p>}
              <button
                type="button"
                className="btn-primary"
                style={{ minWidth: 160 }}
                disabled={applying || totalActions === 0}
                onClick={() => void applyDecisions()}
              >
                {applying ? "Bezig met toepassen…" : `Toepassen (${totalActions} ${totalActions === 1 ? "actie" : "acties"})`}
              </button>
              {totalActions === 0 && (
                <span className="page-subtitle" style={{ margin: 0 }}>
                  Geen wijzigingen geselecteerd.
                </span>
              )}
            </div>
          )}

          {applyResult && (
            <div className="detail-section" style={{ maxWidth: 480, marginTop: "1.5rem", background: "var(--success-bg, #f0fff4)" }}>
              <h3 className="section-title">Toegepast ✓</h3>
              <ul style={{ margin: 0, padding: "0 0 0 1.2rem" }}>
                <li>Lijsten bijgewerkt: <strong>{applyResult.lists_updated}</strong></li>
                <li>Gekoppeld: <strong>{applyResult.linked}</strong></li>
                <li>Nieuw aangemaakt: <strong>{applyResult.created}</strong></li>
              </ul>
              {applyResult.errors.length > 0 && (
                <p className="form-error" style={{ marginTop: "0.5rem" }}>
                  Fouten: {applyResult.errors.join("; ")}
                </p>
              )}
              <button type="button" className="btn-secondary" style={{ marginTop: "1rem" }} onClick={() => void loadPreview()}>
                Opnieuw controleren
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const TH: React.CSSProperties = {
  textAlign: "left",
  padding: "0.45rem 0.75rem",
  fontWeight: 600,
  fontSize: "0.82rem",
  color: "var(--muted, #555)",
  whiteSpace: "nowrap",
  borderBottom: "2px solid var(--border, #e5e7eb)",
};

const TD: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  verticalAlign: "middle",
};

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; color: string }> = {
    active: { bg: "#e6f4ea", color: "#1a7a3f" },
    unsubscribed: { bg: "#fdecea", color: "#b91c1c" },
    bounced: { bg: "#fff3cd", color: "#92400e" },
    junk: { bg: "#fdecea", color: "#b91c1c" },
    unconfirmed: { bg: "#f3f4f6", color: "#555" },
  };
  const c = colors[status] ?? { bg: "#f3f4f6", color: "#555" };
  return (
    <span style={{ background: c.bg, color: c.color, padding: "2px 8px", borderRadius: 9999, fontSize: "0.76rem", fontWeight: 600 }}>
      {status}
    </span>
  );
}
